import React from 'react'
import {
  useFrameHeight,
  getNextMoveIndex,
  getPrevMoveIndex,
  getDefaultSlideIndex,
  getBoundedIndex,
  getPercentOffsetForSlide
} from './carousel-helpers'
import { CarouselProps } from './carousel-props'
import { carouselContext } from './carousel-context'

export function Carousel<T>({
  adaptiveHeight = false,
  afterSlide = () => {
    // do nothing
  },
  beforeSlide = () => {
    // do nothing
  },
  cellAlign = 'left',
  speed = 500,
  disableAnimation = false,
  slidesToScroll = 1,
  slidesToShow = 1,
  wrapAround = false,
  children = <></>,
  slideIndex,
  slides,

  style,
  className
}: CarouselProps<T>) {
  const carouselId = React.useId()
  const slideCount = slides.length

  const slideViewpointRef = React.useRef<HTMLDivElement | null>(null)
  const slideContainerRef = React.useRef<HTMLDivElement>(null)
  const isMounted = React.useRef<boolean>(true)

  const [currentSlide, setCurrentSlide] = React.useState<number>(() =>
    getDefaultSlideIndex(slideIndex, slideCount, slidesToShow, slidesToScroll, cellAlign)
  )
  const [animationDistance, setAnimationDistance] = React.useState<number>(0)

  React.useEffect(() => {
    isMounted.current = true
    return () => {
      isMounted.current = false
    }
  }, [])

  const goToSlide = React.useCallback(
    (targetSlideUnbounded: number) => {
      if (!slideViewpointRef.current || !slideContainerRef.current) return

      const targetSlideBounded = getBoundedIndex(targetSlideUnbounded, slideCount)

      const slideChanged = targetSlideUnbounded !== currentSlide
      slideChanged && beforeSlide(currentSlide, targetSlideBounded)

      // Calculate the distance the slide transition animation needs to cover.
      const currentOffset =
        slideViewpointRef.current.getBoundingClientRect().left - slideContainerRef.current.getBoundingClientRect().left
      const sliderWidth = slideViewpointRef.current.offsetWidth
      let targetOffset =
        (getPercentOffsetForSlide(targetSlideBounded, slideCount, slidesToShow, cellAlign, wrapAround) / 100) * sliderWidth
      if (wrapAround) {
        // We have to do a bit of a recovery effort to figure out the closest
        // offset based on the direction we're going in the slides. The reason
        // it's complicated is because, when wrapped, both the current offset
        // and the calculated target offset are based on bounded slide indices -
        // that is, when wrapping, we often skip back to the first or last slide
        // seamlessly to make the carousel appear to infinitely repeat

        // The DOM width of `slideCount` slides
        const slideSetWidth = sliderWidth / 3

        if (targetSlideUnbounded < 0) {
          targetOffset += slideSetWidth
        }
        if (targetSlideUnbounded >= slideCount) {
          targetOffset -= slideSetWidth
        }
      }

      setAnimationDistance(targetOffset - currentOffset)

      if (slideChanged) {
        setCurrentSlide(targetSlideBounded)

        // if animation is disabled decrease the speed to 40
        const msToEndOfAnimation = !disableAnimation ? speed || 500 : 40
        setTimeout(() => {
          if (!isMounted.current) return
          afterSlide(targetSlideBounded)
        }, msToEndOfAnimation)
      }
    },
    [
      afterSlide,
      beforeSlide,
      slideContainerRef,
      cellAlign,
      currentSlide,
      disableAnimation,
      speed,
      slideCount,
      slidesToShow,
      wrapAround
    ]
  )

  const nextSlide = React.useCallback(() => {
    const nextSlideIndex = getNextMoveIndex(wrapAround, currentSlide, slideCount, slidesToScroll, slidesToShow, cellAlign)

    if (currentSlide !== nextSlideIndex) {
      goToSlide(nextSlideIndex)
    }
  }, [cellAlign, currentSlide, goToSlide, slidesToScroll, slideCount, slidesToShow, wrapAround])

  const prevSlide = React.useCallback(() => {
    const prevSlideIndex = getPrevMoveIndex(wrapAround, currentSlide, slidesToScroll, slidesToShow, cellAlign)

    if (currentSlide !== prevSlideIndex) {
      goToSlide(prevSlideIndex)
    }
  }, [cellAlign, currentSlide, goToSlide, slidesToScroll, slidesToShow, wrapAround])

  // When user changed the slideIndex property from outside.
  const prevMovedToSlideIndex = React.useRef(slideIndex)
  React.useEffect(() => {
    if (slideIndex !== undefined && slideIndex !== prevMovedToSlideIndex.current) {
      goToSlide(slideIndex)
      prevMovedToSlideIndex.current = slideIndex
    }
  }, [slideIndex, goToSlide])

  const { frameHeight, handleVisibleSlideHeightChange, initializedAdaptiveHeight } = useFrameHeight(
    adaptiveHeight,
    slidesToShow,
    slideCount
  )

  return (
    <carouselContext.Provider
      value={{
        wrapAround,
        slideCount,
        slidesToShow,
        cellAlign,
        slidesToScroll,
        currentSlide,
        animationDistance,
        disableAnimation,
        speed,
        frameHeight,
        nextSlide,
        prevSlide,
        goToSlide,
        slides,
        slideContainerRef,
        slideViewpointRef,
        carouselId,
        initializedAdaptiveHeight,
        adaptiveHeight,
        onVisibleSlideHeightChange: handleVisibleSlideHeightChange
      }}
    >
      <div
        style={{
          position: 'relative',
          ...style
        }}
        role={'group'}
        aria-roledescription="carousel"
        className={className}
      >
        {children}
      </div>
    </carouselContext.Provider>
  )
}

Carousel.displayName = 'Carousel'
